import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '../../services/api';
import useOptimisticMutation from '../useOptimisticMutation';
import useToaster from '../useToaster';
import useAccount from './useAccount';

const useClientComments = ({ clientId }) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const storeKey = ['client-comments', accountId, clientId];
  const queryClient = useQueryClient();
  const { addSuccess, addResponseError } = useToaster();

  const { isLoading, data } = useQuery(storeKey, () => api.fetchClientComments({ accountId, clientId }));

  const { mutateAsync: deleteComment } = useOptimisticMutation(
    storeKey,
    ({ id }) => api.deleteClientComment({ accountId, clientId, id }),
    ({ previousData, id }) => {
      return {
        data: (previousData?.data || []).filter(({ id: currentId }) => currentId !== id)
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
        addSuccess(t('successDeleteComment'));
      },
      onError: (err) => {
        addResponseError(err);
      }
    }
  );

  const { mutateAsync: addComment } = useMutation(
    ({ comment }) => api.addClientComment({ accountId, clientId, comment }),
    {
      throwOnError: true,
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
        addSuccess(t('successAddingComment'));
      },
      onError: (err) => {
        addResponseError(err);
      }
    }
  );

  return {
    isLoading,
    data: data?.data || [],
    deleteComment,
    addComment
  };
};

export default useClientComments;
